import { Rect, Text, Group, Textbox } from "fabric";

/* ============================================
   常數設定 (響應式設計)
   這些常數定義了元件的間距與樣式，但不再包含固定寬度
=============================================== */
const PADDING_X = 20; 
const PADDING_Y = 5; 
const GAP_MD = 6; 
const GAP_SM = 4;  
const BUTTON_HEIGHT = 46;

/* ============================================
   判斷是否為按鈕 Group
=============================================== */
export function isButtonGroup(obj) {
  return !!obj && Array.isArray(obj._objects) && obj._objects.length >= 2 && obj.type === "group";
}

/* ============================================
   匯入圖片
=============================================== */
export function importImage(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject("No file");
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ============================================
   建立/更新 標題文字物件
=============================================== */
function createOrUpdateText(canvas, props) {
  let txt = canvas.getObjects().find(o => o.type === "textbox");
  
  // 動態計算當前 Canvas 的內容寬度
  const currentCanvasWidth = canvas.getWidth();
  const currentContentWidth = currentCanvasWidth - (PADDING_X * 2);

  const textValue = props.text || " ";
  const fontFamily = props.fontFamily === "Microsoft JhengHei" 
    ? "Microsoft JhengHei, 'Noto Sans TC', Arial, sans-serif" 
    : props.fontFamily;

  if (!txt) {
    txt = new Textbox(textValue, {
      left: currentCanvasWidth / 2,
      top: PADDING_Y,
      width: currentContentWidth,
      fill: props.color || "#000000",
      fontSize: Number(props.fontSize) || 22,
      fontFamily: fontFamily,
      fontWeight: "bold",
      originX: "center",
      originY: "top", // 改為上方對齊，方便堆疊計算
      textAlign: "center",
      selectable: true,
      hasControls: false,
      hasBorders: false,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      hoverCursor: "default",
      splitByGrapheme: true,
      lineHeight: 1.4,
      editable: false,
      objectCaching: false,
    });
    canvas.add(txt);
  } else {
    // 更新屬性
    txt.set({
      text: textValue,
      fill: props.color,
      fontSize: Number(props.fontSize),
      fontFamily: fontFamily,
      width: currentContentWidth,
      left: currentCanvasWidth / 2,
    });
  }
  return txt;
}

/* ============================================
   建立/更新 按鈕物件
=============================================== */
function createOrUpdateButton(canvas, btnData, index, layoutStyle, yPos) {
  // 尋找現有的按鈕 Group
  const groups = canvas.getObjects().filter(o => isButtonGroup(o));
  let group = groups.find(g => g.buttonIndex === index);

  // 動態計算當前 Canvas 的內容寬度
  const currentCanvasWidth = canvas.getWidth();
  const currentContentWidth = currentCanvasWidth - (PADDING_X * 2);

  // 計算按鈕寬度與位置
  let width = currentContentWidth;
  
  if (layoutStyle === "horizontal") {
    width = (currentContentWidth - GAP_SM) / 2; 
  }
  
  // 置中對齊邏輯 (無論內容多寬，整體置中於 Canvas)
  let left = currentCanvasWidth / 2;

  if (layoutStyle === "horizontal") {
     if (index === 0) left = (currentCanvasWidth / 2) - (width / 2) - (GAP_SM / 2);
     else left = (currentCanvasWidth / 2) + (width / 2) + (GAP_SM / 2);
  }

  const btnText = btnData.text || "按鈕";
  const bgColor = btnData.bgColor || "#000000";
  const textColor = btnData.textColor || "#ffffff";
  const fontSize = Number(btnData.fontSize) || 16;

  if (group) {
    // === 更新現有按鈕 ===
    const rect = group._objects[0];
    const label = group._objects[1];
    
    rect.set({ width, height: BUTTON_HEIGHT, fill: bgColor });
    label.set({ text: btnText, fill: textColor, fontSize });
    
    // 重新定位 Label
    label.set({ left: 0, top: 0, originX: "center", originY: "center" });
    
    group.set({
      left,
      top: yPos + (BUTTON_HEIGHT / 2), // Group origin is center
      width,
      height: BUTTON_HEIGHT
    });
    group.setCoords(); // 確保點擊區域更新
  } else {
    // === 建立新按鈕 ===
    const rect = new Rect({
      width,
      height: BUTTON_HEIGHT,
      fill: bgColor,
      rx: 6, // 稍微圓一點，視覺上較為柔和
      ry: 6,
      originX: "center",
      originY: "center",
    });

    const label = new Text(btnText, {
      fill: textColor,
      fontSize,
      fontFamily: "Microsoft JhengHei",
      originX: "center",
      originY: "center",
      textAlign: "center",
    });

    group = new Group([rect, label], {
      left,
      top: yPos + (BUTTON_HEIGHT / 2),
      width,
      height: BUTTON_HEIGHT,
      originX: "center",
      originY: "center",
      hasControls: false,
      hasBorders: false,
      selectable: true,
      subTargetCheck: true,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      hoverCursor: "pointer", // 按鈕游標
    });
    group.buttonIndex = index;
    canvas.add(group);
  }
  return group;
}

/* ============================================
   核心：自動排版渲染函式 (Auto Layout)
   這會模擬 HTML 的 Flow Layout
=============================================== */
export function renderCanvasLayout(canvas, activePage) {
  if (!canvas || !activePage) return;

  const { layoutStyle, textProps, buttons, canvasBg } = activePage;

  // 1. 設定背景
  canvas.set("backgroundColor", canvasBg || "#ffffff");

  // 2. 渲染/更新 文字
  const txtObj = createOrUpdateText(canvas, textProps);
  
  // 修正：Fabric 的 Textbox 預設緊貼頂部
  // 我們透過微調 PADDING_Y 來視覺對齊
  
  // 強制重新計算文字高度 (Fabric 有時會有延遲)
  txtObj.initDimensions(); 
  const textHeight = txtObj.height * txtObj.scaleY;

  // 3. 計算按鈕起始 Y 座標 (文字底部 + 間距)
  let currentY = PADDING_Y + textHeight + GAP_MD;

  // 4. 清除多餘的按鈕 (例如切換樣式時)
  const existingGroups = canvas.getObjects().filter(o => isButtonGroup(o));
  const neededButtonsCount = layoutStyle === "standard" ? 1 : 2;
  
  // 移除多餘的
  existingGroups.forEach(g => {
    if (g.buttonIndex >= neededButtonsCount) canvas.remove(g);
  });

  // 5. 渲染按鈕
  if (layoutStyle === "horizontal") {
    // 水平排列：兩個按鈕在同一行 (Y 座標相同)
    createOrUpdateButton(canvas, buttons[0], 0, "horizontal", currentY);
    createOrUpdateButton(canvas, buttons[1], 1, "horizontal", currentY);
    currentY += BUTTON_HEIGHT;
  } else {
    // 垂直排列 (standard 或 vertical)
    const btn1 = createOrUpdateButton(canvas, buttons[0], 0, "vertical", currentY);
    currentY += BUTTON_HEIGHT + GAP_SM;

    if (layoutStyle === "vertical") {
      const btn2 = createOrUpdateButton(canvas, buttons[1], 1, "vertical", currentY);
      currentY += BUTTON_HEIGHT; 
    }
  }

  // 6. 根據內容調整 Canvas 高度 (自動長高)
  const finalHeight = currentY + PADDING_Y;
  const minHeight = 100; // 保持一個最小高度
  canvas.setHeight(Math.max(minHeight, finalHeight));

  canvas.requestRenderAll();
}

// 為了相容舊的 editBlock 呼叫，保留轉接函式
export function updateTextProps(textObj, props) {
  // 由於我們改用全域 renderCanvasLayout，這裡其實只要觸發重繪即可
  // 但為了保持單一物件更新的效能，我們還是更新屬性，Layout 由上層 Effect 觸發
  if(!textObj) return;
  // 實際屬性更新已在 renderCanvasLayout 中處理，或是由 React Effect 觸發
}

export function updateButtonProps(group, props) {
   // 同上
}
